import {
  Autocomplete,
  Divider,
  FormControl,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
} from "@mui/material";
import React from "react";
import { useAppSelector } from "../../app/hooks";
import { selectLabels } from "../../slices/language";
import { UnitInfo, UserInfo } from "../RegisterGuardLog/RegisterGuardLog";
import { RequirementTypesData } from "../../api/models/requirement-types";
import RequirementsComponent from "../RequirementsComponent";
import { AccessGroupCurrent } from "../../routes/Logs/Logs";
import PhotoButton from "../PhotoButton";

interface ManualEntranceLogProps {
  hasRegisterAuthorization: boolean;
  visitorName: string;
  setVisitorName: React.Dispatch<React.SetStateAction<string>>;
  countryCode: "+52" | "+1";
  setCountryCode: React.Dispatch<React.SetStateAction<"+52" | "+1">>;
  visitorPhone: string;
  setVisitorPhone: React.Dispatch<React.SetStateAction<string>>;
  defaultUnitsProps: {
    options: UnitInfo[];
    getOptionLabel: (option: UnitInfo) => string;
  };
  unit: UnitInfo | null;
  setUnit: React.Dispatch<React.SetStateAction<UnitInfo | null>>;
  unitInputValue: string;
  setUnitInputValue: React.Dispatch<React.SetStateAction<string>>;
  defaultUsersProps: {
    options: UserInfo[];
    getOptionLabel: (option: UserInfo) => string;
  };
  selectedUser: UserInfo | null;
  setSelectedUser: React.Dispatch<React.SetStateAction<UserInfo | null>>;
  userInputValue: string;
  setUserInputValue: React.Dispatch<React.SetStateAction<string>>;
  motive: string;
  setMotive: React.Dispatch<React.SetStateAction<string>>;
  requirements: RequirementTypesData[] | null;
  requirementsValues: any[];
  setRequirementsValues: React.Dispatch<React.SetStateAction<any[]>>;
  selectedRule: AccessGroupCurrent | null;
  idFile: File | null;
  setIdFile: React.Dispatch<React.SetStateAction<File | null>>;
  plateFile: File | null;
  setPlateFile: React.Dispatch<React.SetStateAction<File | null>>;
  portraitFile: File | null;
  setPortraitFile: React.Dispatch<React.SetStateAction<File | null>>;
  equipmentFile: File | null;
  setEquipmentFile: React.Dispatch<React.SetStateAction<File | null>>;
}

const ManualEntranceLog = ({
  hasRegisterAuthorization,
  visitorName,
  setVisitorName,
  countryCode,
  setCountryCode,
  visitorPhone,
  setVisitorPhone,
  defaultUnitsProps,
  unit,
  setUnit,
  unitInputValue,
  setUnitInputValue,
  defaultUsersProps,
  selectedUser,
  setSelectedUser,
  userInputValue,
  setUserInputValue,
  motive,
  setMotive,
  requirements,
  requirementsValues,
  setRequirementsValues,
  selectedRule,
  idFile,
  setIdFile,
  plateFile,
  setPlateFile,
  portraitFile,
  setPortraitFile,
  equipmentFile,
  setEquipmentFile,
}: ManualEntranceLogProps) => {
  const labels = useAppSelector(selectLabels);

  const countryCodeArray = [
    {
      label: "MX +52",
      value: "+52",
    },
    {
      label: "US +1",
      value: "+1",
    },
  ];

  return (
    <div
      className="w-100 border-box hidden flex flex-dir-col align-items-center gap-6 p-8"
      style={{
        backgroundColor: "white",
        border: "1px #e0e0e0 solid",
        borderRadius: 6,
      }}
    >
      <div className="w-100">
        <p className="t-nowrap body-text-xs mb-3">{labels.full_name} *</p>
        <TextField
          fullWidth
          placeholder={labels.enter_your_name}
          value={visitorName}
          size="small"
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            setVisitorName(event.target.value);
          }}
        />
      </div>
      {!hasRegisterAuthorization && (
        <div className={"w-100"}>
          <p className="t-nowrap body-text-xs mb-3">{labels.telephone}</p>
          <div className="flex align-items-start justify-content-end gap-4">
            <FormControl style={{ width: 150 }}>
              <Select
                value={countryCode}
                onChange={(event: SelectChangeEvent) =>
                  setCountryCode(event.target.value as "+52" | "+1")
                }
                size="small"
              >
                {countryCodeArray.map((item, index) => (
                  <MenuItem key={`${index}-${item?.label}`} value={item?.value}>
                    {item?.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              fullWidth
              type="number"
              placeholder={labels.ten_digit_number}
              value={visitorPhone}
              size="small"
              error={visitorPhone.length > 0 && visitorPhone.length !== 10} // Si no tiene 10 dígitos, muestra error
              helperText={
                visitorPhone.length > 0 && visitorPhone.length !== 10
                  ? "10 dígitos"
                  : ""
              }
              FormHelperTextProps={{
                sx: { textAlign: "right" },
              }}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                setVisitorPhone(event.target.value);
              }}
            />
          </div>
        </div>
      )}
      <div className="w-100">
        <p className="t-nowrap body-text-xs mb-3">{labels.unit} *</p>
        <Autocomplete
          {...defaultUnitsProps}
          autoComplete
          isOptionEqualToValue={(option, value) => option.id === value.id}
          value={unit}
          onChange={(event: any, newValue: UnitInfo | null) => {
            setUnit(newValue);
            if (!newValue || selectedUser?.unit.id !== newValue.id) {
              setSelectedUser(null);
              setUserInputValue("");
            }
          }}
          inputValue={unitInputValue}
          onInputChange={(event, newInputValue) => {
            setUnitInputValue(newInputValue);
          }}
          id="PropertyUnits"
          size="small"
          renderInput={(params) => <TextField {...params} />}
        />
      </div>
      <div className="w-100">
        <p className="t-nowrap body-text-xs mb-3">{labels.user} *</p>
        <Autocomplete
          {...defaultUsersProps}
          autoComplete
          isOptionEqualToValue={(option, value) => option.id === value.id}
          renderOption={(props, option) => (
            <li {...props} key={option.id}>{option.label} — {option.unit.label}</li>
          )}
          value={selectedUser}
          onChange={(event: any, newValue: UserInfo | null) => {
            setSelectedUser(newValue);
            if (newValue) {
              setUnit(newValue.unit);
              setUnitInputValue(newValue.unit.label);
            }
          }}
          inputValue={userInputValue}
          onInputChange={(event, newInputValue) => {
            setUserInputValue(newInputValue);
          }}
          id="UsersByUnit"
          size="small"
          renderInput={(params) => <TextField {...params} />}
        />
      </div>
      <div className="w-100">
        <p className="t-nowrap body-text-xs mb-3">{labels.motive} *</p>
        <TextField
          fullWidth
          // placeholder={labels.enter_your_name}
          value={motive}
          size="small"
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            setMotive(event.target.value);
          }}
        />
      </div>
      {hasRegisterAuthorization && requirements && requirements.length > 0 && (
        <div className="w-100 border-box flex flex-dir-col gap-6">
          {requirements.map((item, index) => (
            <RequirementsComponent
              key={`${item?.id}-${index}`}
              requirement={item}
              requirementIndex={index}
              value={requirementsValues}
              setValue={setRequirementsValues}
            />
          ))}
        </div>
      )}
      {hasRegisterAuthorization &&
        selectedRule &&
        selectedRule.photographs.length > 0 && (
          <>
            <Divider className="w-100">
              <p className="subtitle-color font-14">
                {labels.required_photographs} *
              </p>
            </Divider>
            <div className="w-100 border-box flex align-items-center justify-content-evenly gap-6 mt-4">
              {selectedRule.photographs.map((item, index) => (
                <PhotoButton
                  key={`${item}-${index}`}
                  photoType={item as "id" | "plates" | "visitor" | "other"}
                  file={
                    item === "id"
                      ? idFile
                      : item === "plates"
                        ? plateFile
                        : item === "visitor"
                          ? portraitFile
                          : item === "other"
                            ? equipmentFile
                            : null
                  }
                  setFile={
                    item === "id"
                      ? setIdFile
                      : item === "plates"
                        ? setPlateFile
                        : item === "visitor"
                          ? setPortraitFile
                          : item === "other"
                            ? setEquipmentFile
                            : () => {}
                  }
                />
              ))}
            </div>
          </>
        )}
    </div>
  );
};

export default ManualEntranceLog;
